/**
 * "Go to production" — Guides section (ADR-0074); extended into the full
 * go-live checklist by B27.1 (2026-06-12): end-user auth verification, the
 * customer's OWN deployer obligations to THEIR users (GDPR/AI-Act — the
 * who-handles-what matrix on /trust/ai), rate-limit prep + error-comms
 * guidance, and the nightly cross-tenant probe trust story.
 * Composes the test → live transition: switch environment, mint live keys, choose
 * a plan, set guardrails. Links to environments, API keys, plans, and limits.
 */
import type { DocPageContent } from '../types';

export const GUIDES_GO_TO_PRODUCTION_PAGE: DocPageContent = {
  slug: 'guides/go-to-production',
  titleKey: 'docs.guides.production.title',
  descriptionKey: 'docs.guides.production.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.guides.production.lead' },

    {
      kind: 'heading',
      level: 2,
      id: 'checklist',
      textKey: 'docs.guides.production.checklist.heading',
    },
    {
      kind: 'steps',
      steps: [
        {
          titleKey: 'docs.guides.production.step.env.title',
          textKey: 'docs.guides.production.step.env.text',
        },
        {
          titleKey: 'docs.guides.production.step.keys.title',
          textKey: 'docs.guides.production.step.keys.text',
        },
        {
          titleKey: 'docs.guides.production.step.origins.title',
          textKey: 'docs.guides.production.step.origins.text',
        },
        {
          titleKey: 'docs.guides.production.step.plan.title',
          textKey: 'docs.guides.production.step.plan.text',
        },
        {
          titleKey: 'docs.guides.production.step.guardrails.title',
          textKey: 'docs.guides.production.step.guardrails.text',
        },
        {
          titleKey: 'docs.guides.production.step.endUserAuth.title',
          textKey: 'docs.guides.production.step.endUserAuth.text',
        },
      ],
    },
    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.guides.production.keys.title',
      textKey: 'docs.guides.production.keys.body',
      links: {
        keys: {
          target: { docSlug: 'auth/api-keys' },
          labelKey: 'docs.guides.production.keys.link',
        },
      },
    },

    {
      kind: 'heading',
      level: 2,
      id: 'your-obligations',
      textKey: 'docs.guides.production.deployer.heading',
    },
    {
      kind: 'paragraph',
      textKey: 'docs.guides.production.deployer.body',
      links: {
        matrix: {
          target: { docSlug: 'trust/ai' },
          labelKey: 'docs.guides.production.deployer.matrixLink',
        },
      },
    },
    { kind: 'paragraph', textKey: 'docs.guides.production.deployer.privacy' },

    {
      kind: 'heading',
      level: 2,
      id: 'rate-limits',
      textKey: 'docs.guides.production.limits.heading',
    },
    { kind: 'paragraph', textKey: 'docs.guides.production.limits.body' },
    { kind: 'paragraph', textKey: 'docs.guides.production.limits.comms' },

    {
      kind: 'heading',
      level: 2,
      id: 'isolation-probes',
      textKey: 'docs.guides.production.probes.heading',
    },
    {
      kind: 'paragraph',
      textKey: 'docs.guides.production.probes.body',
      links: {
        trust: {
          target: { docSlug: 'trust/data' },
          labelKey: 'docs.guides.production.probes.trustLink',
        },
      },
    },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.guides.production.related.environments.title',
          textKey: 'docs.guides.production.related.environments.text',
          to: { docSlug: 'dashboard/environments' },
        },
        {
          titleKey: 'docs.guides.production.related.plans.title',
          textKey: 'docs.guides.production.related.plans.text',
          to: { docSlug: 'billing/plans' },
        },
        {
          titleKey: 'docs.guides.production.related.limits.title',
          textKey: 'docs.guides.production.related.limits.text',
          to: { docSlug: 'billing/limits' },
        },
      ],
    },
  ],
};
